import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  acceptAllChoices,
  DENIED,
  getConsent,
  hasDecided,
  hasGPC,
  onOpenPrivacyPreferences,
  saveConsent,
  type ConsentCategory,
  type ConsentChoices,
} from "@/lib/consent";

const CATEGORIES: { key: ConsentCategory; title: string; description: string }[] = [
  {
    key: "analytics",
    title: "Analytics / Measurement",
    description:
      "Helps us measure website usage and performance. If declined, Google may still receive limited cookieless measurement signals for aggregate reporting under Google's Consent Mode.",
  },
  {
    key: "experience_analytics",
    title: "Experience Analytics",
    description:
      "Helps us understand website behavior and improve the visitor experience, including session analytics and replay.",
  },
  {
    key: "business_identification",
    title: "Business Visitor Identification",
    description:
      "Helps us understand which organizations and, where permitted, business visitors are interested in The Famous Group's products and services.",
  },
  {
    key: "marketing",
    title: "Marketing",
    description:
      "Controls optional advertising and remarketing technologies if these are added in the future.",
  },
];

const btnBase =
  "inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";
const btnPrimary = `${btnBase} bg-foreground text-background hover:bg-foreground/85`;
const btnSecondary = `${btnBase} border border-foreground/30 text-foreground hover:bg-foreground/10`;
const linkCls =
  "underline underline-offset-2 hover:text-foreground rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const btnSm =
  "inline-flex items-center justify-center rounded-full px-4 py-1.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";
const btnSmPrimary = `${btnSm} bg-foreground text-background hover:bg-foreground/85`;
const btnSmSecondary = `${btnSm} border border-foreground/40 text-foreground hover:bg-foreground/10`;
const btnSmText = `${btnSm} text-muted-foreground hover:text-foreground underline-offset-2 hover:underline`;

const ConsentManager = () => {
  const [bannerOpen, setBannerOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [draft, setDraft] = useState<ConsentChoices>(getConsent());
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const gpc = hasGPC();

  useEffect(() => {
    setBannerOpen(!hasDecided());
  }, []);

  const openPrefs = useCallback(() => {
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    const stored = getConsent();
    // First-time: pre-fill nothing (all off). GPC categories always start off.
    setDraft(hasDecided() ? stored : { ...DENIED });
    setPrefsOpen(true);
  }, []);

  useEffect(() => onOpenPrivacyPreferences(openPrefs), [openPrefs]);

  const commit = (choices: ConsentChoices) => {
    saveConsent(choices);
    setBannerOpen(false);
    setPrefsOpen(false);
  };

  return (
    <>
      {bannerOpen && !prefsOpen && (
        <section
          role="region"
          aria-label="Privacy choices"
          aria-describedby="tfg-consent-body"
          data-analytics-skip
          className="fixed z-[60] left-3 right-3 bottom-3 sm:left-1/2 sm:right-auto sm:bottom-6 sm:-translate-x-1/2 sm:w-[calc(100%-3rem)] sm:max-w-[1000px] rounded-xl border border-border/60 bg-card/85 backdrop-blur-md text-foreground shadow-lg px-4 py-3 sm:px-5 sm:py-3.5 flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-5"
        >
          <p id="tfg-consent-body" className="flex-1 text-xs sm:text-[13px] leading-snug text-muted-foreground">
            <span className="sm:hidden">We use cookies and analytics to improve your experience and understand site usage.</span>
            <span className="hidden sm:inline">We use cookies and analytics to improve your experience and understand how our website is used.</span>{" "}
            <Link to="/privacy" className={linkCls} data-analytics-skip>Privacy Policy</Link>
            {gpc && <span className="block mt-1 text-[11px]">Global Privacy Control detected — identification and marketing stay off.</span>}
          </p>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button type="button" data-analytics-skip className={btnSmPrimary} onClick={() => commit(acceptAllChoices())}>
              Accept
            </button>
            <button type="button" data-analytics-skip className={btnSmSecondary} onClick={() => commit({ ...DENIED })}>
              Decline
            </button>
            <button type="button" data-analytics-skip className={btnSmText} onClick={openPrefs}>
              Manage
            </button>
          </div>
        </section>
      )}

      <Dialog
        open={prefsOpen}
        onOpenChange={(o) => {
          setPrefsOpen(o);
          if (!o && !hasDecided()) setBannerOpen(true);
        }}
      >
        <DialogContent
          data-analytics-skip
          className="max-w-lg max-h-[90vh] overflow-y-auto bg-card border-border"
          onCloseAutoFocus={(e) => {
            const el = returnFocusRef.current;
            if (el && document.contains(el)) {
              e.preventDefault();
              el.focus();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Privacy Preferences</DialogTitle>
            <DialogDescription>
              Choose which technologies we may use. You can change this at any time from “Privacy Choices” in the
              footer.
            </DialogDescription>
          </DialogHeader>

          <ul className="divide-y divide-border">
            <li className="py-4 flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold">Strictly Necessary</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Required for the website to operate securely and to remember your privacy choices.
                </p>
              </div>
              <span className="shrink-0 text-xs font-semibold text-muted-foreground">Always Active</span>
            </li>
            {CATEGORIES.map((c) => {
              const id = `tfg-consent-${c.key}`;
              return (
                <li key={c.key} className="py-4 flex items-start justify-between gap-4">
                  <div>
                    <label htmlFor={id} className="text-sm font-semibold cursor-pointer">
                      {c.title}
                    </label>
                    <p id={`${id}-desc`} className="mt-1 text-xs text-muted-foreground">
                      {c.description}
                    </p>
                  </div>
                  <Switch
                    id={id}
                    data-analytics-skip
                    aria-describedby={`${id}-desc`}
                    checked={draft[c.key]}
                    onCheckedChange={(v) => setDraft((d) => ({ ...d, [c.key]: v }))}
                  />
                </li>
              );
            })}
          </ul>

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <button type="button" data-analytics-skip className={`${btnPrimary} flex-1`} onClick={() => commit(draft)}>
              Save Preferences
            </button>
            <button type="button" data-analytics-skip className={`${btnSecondary} flex-1`} onClick={() => commit(acceptAllChoices())}>
              Accept All
            </button>
            <button type="button" data-analytics-skip className={`${btnSecondary} flex-1`} onClick={() => commit({ ...DENIED })}>
              Reject Non-Essential
            </button>
          </div>
          <p className="text-xs text-muted-foreground flex gap-4">
            <Link to="/privacy" className={linkCls} data-analytics-skip>Privacy Policy</Link>
            <Link to="/cookies" className={linkCls} data-analytics-skip>Cookie &amp; Tracking Policy</Link>
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConsentManager;
